import { useEffect, useState, useCallback } from "react";
import AnnouncementModel from "../models/announcementModel";
import "./ChatAnnouncement.css";

// Per-user dismissals are remembered locally so a closed strip stays closed.
const DISMISSED_KEY = "addy-sidebar-dismissed-announcements";

const getDismissedIds = () => {
  try {
    const stored = localStorage.getItem(DISMISSED_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const addDismissedId = (id) => {
  try {
    const ids = getDismissedIds();
    if (!ids.includes(id)) {
      localStorage.setItem(DISMISSED_KEY, JSON.stringify([...ids, id]));
    }
  } catch {
    // Ignore storage errors (private mode, quota); dismissal just won't persist.
  }
};

/**
 * ChatAnnouncement
 *
 * Compact, dismissible announcement strip docked at the top of the chat
 * composer. Self-fetches the latest active "chat" surface announcement and
 * renders a title plus an optional call-to-action link (opens in a new tab).
 * Renders nothing when there is no active announcement or it was dismissed.
 *
 * `uid` and `email` identify the viewer and `organizationId` says which
 * workspace they are in, so the backend can honour announcements targeted at
 * specific organizations or at an integration's clients.
 *
 * The organization can arrive asynchronously, and asking before it is known
 * gets the audience judged against every organization the account can reach —
 * often a "yes" where the scoped answer is "no", which would show the strip and
 * then take it away. `ready` says the organization has settled; until then this
 * renders nothing and makes no request.
 */
const ChatAnnouncement = ({ uid, email, organizationId, ready = true }) => {
  const [announcement, setAnnouncement] = useState(null);

  useEffect(() => {
    if (!ready) return undefined;
    let isMounted = true;
    AnnouncementModel.getLatestForSurface("chat", {
      uid,
      email,
      organizationId,
    }).then((data) => {
      if (!isMounted || !data) return;
      if (getDismissedIds().includes(data.id)) return;
      setAnnouncement(data);
    });
    return () => {
      isMounted = false;
    };
  }, [ready, uid, email, organizationId]);

  const handleClose = useCallback(() => {
    setAnnouncement((current) => {
      if (current) addDismissedId(current.id);
      return null;
    });
  }, []);

  if (!announcement) return null;

  const hasLink = Boolean(announcement.linkText && announcement.linkUrl);

  return (
    <div className="chat-announcement" role="status">
      <span className="chat-announcement-title" title={announcement.title}>
        {announcement.title}
      </span>
      <span className="chat-announcement-actions">
        {hasLink && (
          <a
            className="chat-announcement-link"
            href={announcement.linkUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {announcement.linkText}
          </a>
        )}
        <button
          type="button"
          className="chat-announcement-close"
          aria-label="Dismiss announcement"
          onClick={handleClose}
        >
          &times;
        </button>
      </span>
    </div>
  );
};

export default ChatAnnouncement;
